import { FC, useCallback, memo } from 'react';
import { batch } from 'react-redux';
import { TopNavbarProps } from './';
import Icon, { Icons } from '../Icon';
import { useTranslations } from '@hooks/useTranslations';
import { useCustomRouter } from '@hooks/useCustomRouter';
import { useLoginAndSaveDeal } from '@hooks/useLoginAndSaveDeal';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { useGTM } from '@hooks/useGTM';
import { FEATURES_LIST } from '../../context/featureSwitchApp';
import { isBrandAC, isBrandAP, isBrandDS, isBrandOV, isMarketFR, isMarketGB } from '../../constants/main';
import { useDispatch, useSelector } from 'react-redux';
import { ICarDetail } from '../../redux/carDetails/carDetails.duck.interface';
import { useCreateDeal } from '@hooks/useCreateDeal';
import { GtmData } from '../../types/gtm';
import CarDetailsDuck from 'src/redux/carDetails/carDetails.duck';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { Redux } from 'src/redux/redux.interface';
import { useExperiment } from '@hooks/useExperiment';

const TopNavbarTemplate: FC<TopNavbarProps> = memo(
    ({ className, showSaveButton, title, onGoBack, step, isLoading, isBackButtonHidden = false, isBasket }) => {
        const { t } = useTranslations();
        const router = useCustomRouter();
        const dispatch = useDispatch();
        const { pushUserAction, getBodyStyleLabel, pushShareConfigurationSuccess, pushToDataLayer } = useGTM();
        const [dealSaved, loginAndSaveDeal] = useLoginAndSaveDeal({ form_type: 'lead' });
        const isDealSaved = useSelector((state) => UIDuck.getOwnState(state).isDealSaved);
        const carDetails: ICarDetail = useSelector((state: Redux) => CarDetailsDuck.getCurrentCarDetails(state));
        const createDeal = useCreateDeal();
        const { getPageNameByUrl } = useExperiment();
        const page = getPageNameByUrl();

        const handleGoBack = useCallback(() => {
            if (isLoading) return;

            if (onGoBack) {
                onGoBack();
            } else {
                router.back();
            }
        }, [router, onGoBack, isLoading]);

        const isBrandRecommendsJourneyAllowed = useFeatureSwitchEnabled(
            FEATURES_LIST.FEATURE_SWITCH_BRAND_RECOMMENDS_ENABLED
        );

        return (
            <div className={className}>
                <div className="topnavbar-grid">
                    <button
                        className={`topnavbar-returnlink${isBackButtonHidden ? '__hidden' : ''}`}
                        onClick={handleGoBack}
                        data-testid="TESTING_BACK"
                    >
                        <Icon auto name={Icons.ChevronUp} />
                        <span className="topnavbar-returnlink__text">{t('link.back')}</span>
                    </button>
                    <div className="content">
                        <h1 className="content-title">
                            {step && step.trim() !== '' && <span className="content-step">{step}</span>}
                            {title}
                        </h1>
                    </div>

                    {showSaveButton && !isBrandRecommendsJourneyAllowed && (
                        <button
                            className="save-button"
                            // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                            onClick={() => {
                                if (!isBasket) {
                                    pushToDataLayer({
                                        eventCategory: 'SolRetail::Basket',
                                        eventAction: 'Validate::SaveMyCar',
                                        event: 'uaevent',
                                        eventLabel: isDealSaved
                                            ? t('topNavbar.savebuttonSaved')
                                            : t('topNavbar.savebutton'),
                                    } as GtmData);
                                }
                                batch(async () => {
                                    dispatch(UIDuck.setLoading(5));
                                    const deal = await createDeal();
                                    dispatch(UIDuck.setIsDealSaved(true));
                                    dispatch(UIDuck.setLoading(-5));
                                    if (!isBasket) {
                                        pushToDataLayer({
                                            eventCategory: 'SolRetail::Basket',
                                            eventAction: 'SaveMyCar',
                                            event: 'uaevent',
                                            eventLabel: getBodyStyleLabel(carDetails),
                                        } as GtmData);
                                    }
                                    if (
                                        Boolean(
                                            (isBrandOV || isBrandAP || isBrandAC || isBrandDS) &&
                                                (isMarketGB || isMarketFR)
                                        )
                                    ) {
                                        pushShareConfigurationSuccess(
                                            'basket',
                                            isDealSaved ? t('topNavbar.savebuttonSaved') : t('topNavbar.savebutton'),
                                            'clickOnSave'
                                        );
                                    }
                                    loginAndSaveDeal(deal.financeQuoteId, page);
                                });
                            }}
                            disabled={isDealSaved || isLoading}
                            data-testid={'TESTING_TOP_BAR_SAVE_BUTTON'}
                        >
                            {isDealSaved ? t('topNavbar.savebuttonSaved') : t('topNavbar.savebutton')}
                        </button>
                    )}
                </div>
            </div>
        );
    }
);
TopNavbarTemplate.displayName = 'TopNavbarTemplate';
export default TopNavbarTemplate;
